/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.kiloparsec

/**
 * Kiloparsec interface to call remote methods.
 *
 * It is used as for secure layer (L1) as for underlying
 * service layer L0. When using [KiloClientConnection], [KiloServerConnection] and [KiloClient]
 * it always implement secure layer, L1.
 */
interface RemoteInterface {
    /**
     * General channel exception
     */
    open class Exception(text: String, reason: Throwable? = null) : RuntimeException(text, reason)

    /**
     * Is thrown when the channel is closed, in an attempt to execute a command, also to all pending
     * calls (see [call]). Client code should never throw it. If command handler needs to break connection
     * it should throw [LocalInterface.BreakConnectionException]
     */
    open class ClosedException(t: String = "connection is closed") : Exception(t)

    open class SecurityException(t: String = "invalid remote id and signature") : LocalInterface.BreakConnectionException(t)


    open class InvalidDataException(msg: String="invalid data, can't unpack") : Exception(msg)

    /**
     * Remote call caused an exception thrown while executing it in the remote party. Note that it
     * does not mean the channel state is bad or closed.
     */
    open class RemoteException(
        val code: String,
        val text: String = "remote exception: $code",
        val extra: UByteArray? = null
    ) : Exception(text) {
        constructor(remoteError: Transport.Block.Error) : this(remoteError.code, remoteError.message, remoteError.extra)
    }

    /**
     * Command is not supported by the remote party
     */
    class UnknownCommand(commandName: String) : RemoteException("UnknownCommand: $commandName")

    open class InternalError(code: String="0"): RemoteException("Internal error: $code")

    suspend fun <R> call(cmd: Command<Unit, R>): R = call(cmd, Unit)

    /**
     * Call the remote procedure with specified [args] and return its result of type [R]. The calling coroutine
     * suspends until the request is performed on the remote side and the result value (also `Unit`) will be received.
     *
     * When it is not necessary to wait for the return value and/or command execution, it is recommended to
     * use [push] instead.
     *
     *  @throws RemoteException if the execution caused exception on the remote size
     *  @throws Exception for registered exceptions, see [LocalInterface.registerError], etc.
     */
    suspend fun <A, R> call(cmd: Command<A, R>, args: A): R

    /**
     * Push the notification without waiting for reception or processing.
     * It returns immediately after sending data to the transport (e.g., to the network).
     * Use [call] if it is necessary to wait until the command will be received and processed by the remote.
     *
     * Push is onlu available for commands without returned value.
     */
    suspend fun <A> push(cmd: Command<A, Unit>, args: A)

    /**
     * Push the command with no args.
     * It returns immediately after sending data to the transport (e.g., to the network).
     * Use [call] if it is necessary to wait until the command will be received and processed by the remote.
     */
    suspend fun push(cmd: Command<Unit,Unit>) = push(cmd,Unit)
}